import api from '../httpClient';

export type DailyPulseResponseType = 'rating' | 'boolean' | 'number' | 'duration' | 'time';
export type DailyPulseStatus = 'due' | 'saved' | 'skipped' | 'missed' | 'disabled';
export type DailyPulseAnswerValue = number | boolean | string;
export type DailyPulseTopic = 'mindset' | 'connection' | 'movement' | 'sleep' | 'nutrition' | 'focus' | 'money' | 'other';

export interface DailyPulseQuestion {
  key: string;
  prompt: string;
  response_type: DailyPulseResponseType;
  low_label: string;
  high_label: string;
  display_order: number;
  version: number;
  enabled: boolean;
  section?: string;
  topic?: DailyPulseTopic;
  unit?: string;
  min_value?: number | null;
  max_value?: number | null;
  step?: number;
  is_legacy?: boolean;
}

export interface DailyPulseEntry {
  id: string;
  date: string;
  outcome: 'saved' | 'skipped';
  timezone: string;
  note: string;
  answers: Record<string, DailyPulseAnswerValue>;
  created_at: string;
  updated_at: string;
}

export interface DailyPulseDay {
  date: string;
  weekday: string;
  day_number: number;
  is_today: boolean;
  status: DailyPulseStatus;
  editable: boolean;
}

export interface DailyCheckInGoalSummary {
  due: number;
  updated: number;
  skipped: number;
  activated: number;
}

export interface DailyPulseOverview {
  version: 1;
  timezone: string;
  today: string;
  editable_from: string;
  target_date: string;
  pulse_due: boolean;
  has_enabled_questions: boolean;
  preferences_configured: boolean;
  enabled_question_keys: string[];
  questions: DailyPulseQuestion[];
  entry: DailyPulseEntry | null;
  today_status: DailyPulseStatus;
  days: DailyPulseDay[];
  goal_summary: DailyCheckInGoalSummary;
}

export interface DailyPulseSavePayload {
  idempotency_key: string;
  answers: Record<string, DailyPulseAnswerValue>;
  note: string;
}

const getOverview = async (targetDate?: string) => (
  await api.get<DailyPulseOverview>('/api/daily-pulse/', {
    params: targetDate ? { date: targetDate } : undefined,
  })
).data;

const savePreferences = async (enabledQuestionKeys: string[]) => (
  await api.put<DailyPulseOverview>('/api/daily-pulse/preferences/', {
    enabled_question_keys: enabledQuestionKeys,
  })
).data;

const saveEntry = async (targetDate: string, payload: DailyPulseSavePayload) => (
  await api.put<{ entry: DailyPulseEntry }>(
    `/api/daily-pulse/entries/${encodeURIComponent(targetDate)}/`,
    payload,
  )
).data.entry;

const skipEntry = async (targetDate: string, idempotencyKey: string) => (
  await api.post<{ entry: DailyPulseEntry }>(
    `/api/daily-pulse/entries/${encodeURIComponent(targetDate)}/skip/`,
    { idempotency_key: idempotencyKey },
  )
).data.entry;

export const dailyPulse = {
  getOverview,
  savePreferences,
  saveEntry,
  skipEntry,
};
